import { NovatiqAuthorizationClient } from '@/_auth-client';
import { exceptionalNavMenuItems, navigationMenuItems, ROUTES, topRightSettingsMenu } from '@/_constants';
import { UserRole } from '@/_constants/novatiq-user-role-constants';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthenticationGuard implements CanActivate, CanActivateChild {
  private readonly authorizationClient = inject(NovatiqAuthorizationClient);
  private readonly router = inject(Router);

  canActivate(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    return this._checkPermissions(activatedRouteSnapshot, state);
  }

  canActivateChild(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    return this._checkPermissions(activatedRouteSnapshot, state);
  }

  private _checkPermissions(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    const data = route.data;

    if (data['isAnonymous']) {
      if (
        state.url === ROUTES.SETUP_2FA.url ||
        state.url === ROUTES.VERIFY_2FA.url ||
        state.url.includes(ROUTES.PASSWORD_EXPIRED.path) ||
        state.url.includes(ROUTES.VERIFY_2FA_EXPIRED.path) ||
        (state.url.includes(ROUTES.CREATE_PASSWORD.path) &&
          (state.url.includes('fromExpired=true') || state.url.includes('token=')))
      ) {
        return true;
      } else if (!this.authorizationClient.isAuthenticated || this.authorizationClient.sessionExpired) {
        this.authorizationClient.removeUserSession();
        return true;
      } else {
        return this.authorizationClient.getLandingPageUrl().pipe(
          map((url) => {
            this.router.navigate([url]);
            return false;
          }),
          catchError(() => {
            this.router.navigate([this.authorizationClient.landingPage]);
            return of(false);
          })
        );
      }
    } else if (this.authorizationClient.isAuthenticated) {
      if (this.authorizationClient.sessionExpired) {
        return this.navigateTo(ROUTES.LOGIN.path);
      } else if (this.isAdminRoute(route)) {
        const currentRouteName = this.resolveRouteNameFromUrl(state.url);
        //#URM#TODO;
        const exp = exceptionalNavMenuItems.find((navmenu) => state.url.includes(navmenu.routerLink[0]));
        if (
          this.authorizationClient.userAccessInfo?.some(({ functionName }) => functionName === currentRouteName) ||
          this.authorizationClient.isNovatiqSuperAdmin
        ) {
          return true;
        } else if (this.authorizationClient.isAdmin && UserRole.ADMIN.includes(currentRouteName)) {
          return true;
        } else if (exp) {
          return true;
        } else {
          this.navigateTo(ROUTES.PAGE_401.path);
        }
      }

      return true;
    }

    return this.navigateTo(ROUTES.LOGIN.path);
  }

  /** Walks the route tree so nested administration URLs inherit `isAdminRoutes` from the parent. */
  private isAdminRoute(route: ActivatedRouteSnapshot): boolean {
    for (let current: ActivatedRouteSnapshot | null = route; current; current = current.parent) {
      if (current.data['isAdminRoutes']) {
        return true;
      }
    }
    return false;
  }

  /**
   * Resolves the menu function name for a URL, including nested child paths
   * (e.g. `/administration/organisations/exportAudiences` → Organisation Management).
   */
  private resolveRouteNameFromUrl(url: string): string | undefined {
    const menus = [...navigationMenuItems, ...topRightSettingsMenu];

    return menus
      .filter((item) => {
        const link = item.routerLink?.[0];
        return link != null && (url === link || url.startsWith(`${link}/`));
      })
      .sort((a, b) => (b.routerLink?.[0]?.length ?? 0) - (a.routerLink?.[0]?.length ?? 0))[0]?.name;
  }

  private navigateTo(destination: string) {
    this.router.navigate([destination]);
    return false;
  }
}
